import { isPlainObject, uniq, upperFirst } from 'lodash';
import { named, quote } from '../texts/words';
import { checkFormDefinition, type Reason } from './check';
import { formDefinition, type FormDefinition } from './form-definition';
import { CURRENT_FORMAT_VERSION, migrate } from './migrations';
import { check } from './schema';

type Opened = { ok: true; definition: FormDefinition } | { ok: false; reasons: Reason[] };

export const openFormDefinition = (stored: unknown): Opened => {
  if (!isPlainObject(stored)) return refused("The form definition isn't a JSON object.");
  const object = stored as Record<string, unknown>;
  const version = object.formatVersion;
  if (version === undefined) return refused(`The form has no format version, ${quote('formatVersion')}.`);
  if (typeof version !== 'number' || !Number.isInteger(version) || version < 1)
    return refused(`The form has the format version ${shown(version)}, which doesn't exist.`);
  if (version > CURRENT_FORMAT_VERSION)
    return refused(
      `The form is in format version ${version}, which is newer than this app reads: ` +
        `it reads format version ${CURRENT_FORMAT_VERSION}.`,
    );
  const migrated = migrate(object, version);
  const shape = check(formDefinition, migrated);
  if (!shape.ok) {
    const texts = shape.issues.map(({ path, message }) => {
      const name = elementAt(migrated, path);
      return name ? `${upperFirst(name)}: ${message}` : message;
    });
    return { ok: false, reasons: uniq(texts).map(text => ({ text })) };
  }
  const reasons = checkFormDefinition(shape.value);
  return reasons.length ? { ok: false, reasons } : { ok: true, definition: shape.value };
};

const refused = (text: string): Opened => ({ ok: false, reasons: [{ text }] });

const shown = (value: unknown): string => {
  if (typeof value === 'string') return quote(value);
  if (Array.isArray(value)) return 'a list';
  if (isPlainObject(value)) return 'an object';
  return String(value);
};

const elementAt = (root: unknown, path: PropertyKey[]): string | undefined => {
  let name: string | undefined;
  let node = root;
  for (const [index, key] of path.entries()) {
    node = isPlainObject(node) || Array.isArray(node) ? (node as Record<PropertyKey, unknown>)[key] : undefined;
    if (path[index - 1] === 'elements' && typeof key === 'number') name = rawElementName(node);
  }
  return name;
};

const rawElementName = (element: unknown): string => {
  if (!isPlainObject(element)) return 'an element';
  const object = element as Record<string, unknown>;
  const text = [object.label, object.title, object.text].find(value => typeof value === 'string');
  const fallback =
    object.kind === 'field' ? 'an untitled field' : object.kind === 'section' ? 'an untitled section' : 'an element';
  return typeof text === 'string' ? named(text, fallback) : fallback;
};
